import React, {useState, useEffect} from 'react';
import Select from '../../../../../components/Select/Select';
import DateTimePicker from '../../../../../components/DateTimePicker/DateTimePicker';
import {OrderType, ScriptStatus, SelectOption} from 'types';
import {parseJSON, format} from 'date-fns';
import styles from '../OrderDetailsModal.module.scss';
import {components} from 'react-select';
import {ScriptStatusOption} from 'components/ScriptStatusSelect/ScriptStatusSelect';
import PriorityFlag from 'components/PriorityFlag';
import useCompanyConfig from 'hooks/useCompanyConfig';
import Typography from 'components/Typography/Typography';
import {Link} from 'react-router-dom';
import {ExternalLinkIcon} from 'pages/RequestDetails/components/icons/external-link';

type Priority = 'medium' | 'high' | 'low';

interface OrderSettingsProps {
  orderNumber: string;
  status: string;
  priority: Priority | null;
  preferenceDate: string | null;
  preferenceTime: string | null;
  description: string | null;
  orderType?: OrderType;
  onStatusChange?: (status: ScriptStatus) => void;
  onPriorityChange?: (priority: any) => void;
  onChangePreferenceTime?: (time: {
    preference_date: string | null;
    preference_time: string | null;
  }) => void;
}

const statusOptions: ScriptStatusOption[] = [
  {value: 'new', label: 'New'},
  {value: 'in_progress', label: 'In-Progress'},
  {value: 'ready_for_dispatch', label: 'Ready for Dispatch'},
  {value: 'completed', label: 'Completed'},
  {value: 'canceled', label: 'Skipped'},
  {value: 'archived', label: 'Archived'},
];

const priorityOptions: SelectOption[] = [
  {value: 'low', label: 'Low Priority'},
  {value: 'medium', label: 'Medium Priority'},
  {value: 'high', label: 'High Priority'},
];

const timeOptions: SelectOption[] = [
  {value: 'morning', label: 'Morning'},
  {value: 'afternoon', label: 'Afternoon'},
  {value: 'evening', label: 'Evening'},
];

const OrderSettings: React.FC<OrderSettingsProps> = ({
  orderNumber,
  status,
  priority,
  preferenceDate,
  preferenceTime,
  description,
  orderType,
  onStatusChange,
  onPriorityChange,
  onChangePreferenceTime,
}) => {
  const {labels} = useCompanyConfig();
  const [date, setDate] = useState<Date | null>(
    preferenceDate ? new Date(preferenceDate) : null
  );
  const [time, setTime] = useState<string>(preferenceTime!);

  useEffect(() => {
    if (preferenceDate) {
      const date = new Date(preferenceDate);
      setDate(date);
    }
  }, [preferenceDate]);

  const onChangeDate = (date: Date | null) => {
    setDate(date);

    onChangePreferenceTime?.({
      preference_date: date ? format(date, 'yyyy-MM-dd') : null,
      preference_time: time || null,
    });
  };
  const onChangeTime = (nextVal: any) => {
    setTime(String(nextVal?.value || ''));
    onChangePreferenceTime?.({
      preference_date: date ? format(date, 'yyyy-MM-dd') : null,
      preference_time: nextVal?.value || null,
    });
  };
  return (
    <div className={styles.card}>
      <div className={styles.sectionHeader}>
        <Link to={`/orders/${orderNumber}`} className={styles.sectionTitleLink}>
          <h2 className={styles.sectionTitle}>
            {orderType === 'pickup' && 'Pickup '}
            {orderType === 'dropoff' && 'Dropoff '}
            {labels.order} Details
            <ExternalLinkIcon className={styles.detailIcon} color="#1A6AE5" />
          </h2>
        </Link>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.formField}>
          <label className={styles.fieldLabel}>Status</label>
          <Select
            size="md"
            placeholder="Select status"
            defaultValue={statusOptions.find(
              (option) => option.value === status
            )}
            onChange={(selectedOption: SelectOption | null) =>
              onStatusChange?.(selectedOption?.value as ScriptStatus)
            }
            options={statusOptions}
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>Priority</label>
          <Select
            size="md"
            placeholder="No priority"
            defaultValue={priorityOptions.find(
              (option) => option.value === priority
            )}
            onChange={(selectedOption: SelectOption | null) =>
              onPriorityChange?.(selectedOption?.value as Priority)
            }
            options={priorityOptions}
            components={{
              SingleValue: (props) => {
                return (
                  <components.SingleValue
                    {...props}
                    className={styles.select_option}
                  >
                    {props.data.label}
                    <PriorityFlag priority={props.data.value as any} />
                  </components.SingleValue>
                );
              },
              Option: (props) => {
                return (
                  <components.Option
                    {...props}
                    className={styles.select_option}
                  >
                    {props.data.label}
                    <PriorityFlag priority={props.data.value as any} />
                  </components.Option>
                );
              },
            }}
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>Preference Date</label>
          <DateTimePicker
            showTimeSelect={false}
            dateFormat="MM/dd/yy E"
            value={date ? parseJSON(date) : undefined}
            onChange={onChangeDate}
            placeholder="Select date"
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>Preference Time</label>
          <Select
            size="md"
            placeholder="Select time"
            defaultValue={timeOptions.find(
              (option) => option.value === preferenceTime || null
            )}
            onChange={onChangeTime}
            options={timeOptions}
          />
        </div>
      </div>
      {!!description && (
        <div className={styles.description}>
          <div className={styles.fieldLabel}>{labels.order} Description:</div>
          <Typography variant="p" className={styles.descriptionText}>
            {description || 'No description'}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default OrderSettings;
